import {
  Button,
  Card,
  DataStatus,
  Dialog,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {HTMLAttributes, useState} from 'react';

import {head, isNil, isNotNil} from 'ramda';

import {
  CreateVehicleFinancingTemplateRequestBody,
  ListVehicleFinancingTemplateResponseBody,
  useCreateVehicleFinancingTemplateMutation,
  useListVehicleFinancingTemplateQuery,
  useReorderAutomaticVehicleFinancingTemplatesMutation,
  useUpdateVehicleFinancingTemplateMutation,
  VehicleFinancingTemplateResponseBody,
} from '@dms/api/sales';
import {SortableCardList} from '@dms/features/dnd';
import {
  buildAutomaticOrderPositions,
  splitVehicleFinancingTemplatesByAutomaticOrder,
  VehicleFinancingTemplateForm,
  VehicleFinancingTemplateFormHelpers,
} from '@dms/features/vehicle-financing-template';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {ApiError, Nullish} from 'shared/web';

import {useStablePendingEditor} from '../hooks/useStablePendingEditor';
import {FinancingTemplateDraft} from '../types/FinancingTemplateDraft';
import {FinancingTemplateSelection} from '../types/FinancingTemplateSelection';
import {PendingEditorChangeHandler} from '../types/PendingEditor';
import {getFinancingTypeLabel} from '../utils/getFinancingTypeLabel';
import {getTemplateTypeLabel} from '../utils/getTemplateTypeLabel';
import {templateRequestBodyToDraft} from '../utils/templateRequestBodyToDraft';
import {EmbeddedTemplateFormFooter} from './EmbeddedTemplateFormFooter';
import {ExistingTemplateCard} from './ExistingTemplateCard';
import {TemplateCardHeader} from './TemplateCardHeader';

const NEW_TEMPLATE_ID = 'new-template';

interface FinancingConfigurationDialogProps {
  isOpen: boolean;
  startWithNewTemplate?: boolean;
  initialTemplateId: string | null;
  fallbackTemplate: VehicleFinancingTemplateResponseBody;
  onCreated?: (template: FinancingTemplateSelection) => Promise<void> | void;
  onUpdated?: (template: FinancingTemplateDraft) => void;
  onPendingChange?: PendingEditorChangeHandler;
  onSavingChange?: (isSaving: boolean) => void;
  onClose: () => void;
}

export function FinancingConfigurationDialog(props: FinancingConfigurationDialogProps) {
  const [canCreateTemplate, canReorderTemplates] = usePermissions({
    permissionKeys: ['createVehicleFinancingTemplate', 'reorderAutomaticVehicleFinancingTemplates'],
  });
  const templatesQuery = useListVehicleFinancingTemplateQuery();
  const [createTemplate] = useCreateVehicleFinancingTemplateMutation();
  const [updateTemplate] = useUpdateVehicleFinancingTemplateMutation();
  const [reorderTemplates, reorderStatus] = useReorderAutomaticVehicleFinancingTemplatesMutation();
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | Nullish>(
    props.startWithNewTemplate ? NEW_TEMPLATE_ID : undefined
  );
  const [isNewTemplateOpen, setIsNewTemplateOpen] = useState(props.startWithNewTemplate === true);
  // Set once the new template exists on the server; the blank card stays visible until the list
  // refetch picks it up, so the user keeps editing the same form instance.
  const [createdTemplateId, setCreatedTemplateId] = useState<string | null>(null);
  // The embedded template form republishes its editor on every dirty↔pristine flip; only the
  // transition is stored here, so a keystroke never re-renders this dialog or the panel above it.
  const {hasPendingEditor, pendingEditor, publishPendingEditor} = useStablePendingEditor(
    props.onPendingChange
  );
  const standardTemplates = splitVehicleFinancingTemplatesByAutomaticOrder(
    templatesQuery.data ?? [],
    'STANDARD'
  );
  const balloonTemplates = splitVehicleFinancingTemplatesByAutomaticOrder(
    templatesQuery.data ?? [],
    'BALLOON'
  );
  const standardActiveNotEnrolledTemplates = standardTemplates.outsideAutomaticOrder.filter(
    (template) => !template.archived
  );
  const balloonActiveNotEnrolledTemplates = balloonTemplates.outsideAutomaticOrder.filter(
    (template) => !template.archived
  );
  const archivedTemplates = [
    ...standardTemplates.outsideAutomaticOrder,
    ...balloonTemplates.outsideAutomaticOrder,
  ].filter((template) => template.archived);
  // Same order as the sections render, so the first visible card opens by default.
  const orderedTemplates = [
    ...standardTemplates.inAutomaticOrder,
    ...standardActiveNotEnrolledTemplates,
    ...balloonTemplates.inAutomaticOrder,
    ...balloonActiveNotEnrolledTemplates,
    ...archivedTemplates,
  ];
  const defaultExpandedTemplateId = props.initialTemplateId ?? head(orderedTemplates)?.id ?? null;
  const activeExpandedTemplateId =
    expandedTemplateId === undefined ? defaultExpandedTemplateId : expandedTemplateId;
  const newTemplateCardId = createdTemplateId ?? NEW_TEMPLATE_ID;
  const isNewTemplateVisible =
    isNewTemplateOpen &&
    (isNil(createdTemplateId) ||
      !templatesQuery.data?.some((template) => template.id === createdTemplateId));
  const expandedTemplateName =
    activeExpandedTemplateId === NEW_TEMPLATE_ID
      ? i18n.t('entity.vehicleFinancingTemplate.labels.createPageTitle')
      : templatesQuery.data?.find((template) => template.id === activeExpandedTemplateId)?.name;

  const discardPendingEditorBefore = (action: () => void) => {
    if (!hasPendingEditor) {
      action();

      return;
    }

    openConfirmDialog({
      text: i18n.t('general.notifications.unsavedChanges.description'),
      onConfirm: () => {
        pendingEditor.discard();
        publishPendingEditor(null);
        action();
      },
    });
  };

  const handleCreateTemplate = () => {
    setIsNewTemplateOpen(true);
    setCreatedTemplateId(null);
    setExpandedTemplateId(NEW_TEMPLATE_ID);
  };

  const finishSave = (helpers: VehicleFinancingTemplateFormHelpers, message: string) => {
    // `reset` with the submitted values clears the dirty state without reverting the inputs.
    helpers.reset(undefined, {keepValues: true});
    publishPendingEditor(null);
    showNotification.success(message);
  };

  const handleNewTemplateSubmit = async (
    requestBody: CreateVehicleFinancingTemplateRequestBody,
    helpers: VehicleFinancingTemplateFormHelpers
  ) => {
    props.onSavingChange?.(true);

    // The saving report is reference-counted, so an unbalanced increment would leave the editor
    // believing a save is still running for the rest of the page's life. `finally` guarantees the
    // decrement even if a rejection escapes the handlers below.
    try {
      await match(createdTemplateId)
        .with(null, () =>
          createTemplate({createVehicleFinancingTemplateRequestBody: requestBody})
            .unwrap()
            .then(async ({id}) => {
              setCreatedTemplateId(id);
              finishSave(helpers, i18n.t('general.notifications.changesSuccessfullySaved'));
              await props.onCreated?.({id, type: requestBody.type});
              setExpandedTemplateId(id);
            })
            .catch((error: ApiError) =>
              handleApiError(error, {signalApiError: helpers.blockerContext?.reportApiFailure})
            )
        )
        .otherwise((templateId) =>
          updateTemplate({
            vehicleFinancingTemplateId: templateId,
            updateVehicleFinancingTemplateRequestBody: requestBody,
          })
            .unwrap()
            .then(() => {
              finishSave(
                helpers,
                i18n.t(
                  'entity.vehicleFinancingTemplate.notifications.changesAppliedToAssignedVehicles'
                )
              );
              props.onUpdated?.(
                templateRequestBodyToDraft(requestBody, {
                  id: templateId,
                  archived: false,
                  automatic: false,
                  automaticPosition: null,
                })
              );
            })
            .catch((error: ApiError) =>
              handleApiError(error, {signalApiError: helpers.blockerContext?.reportApiFailure})
            )
        );
    } finally {
      props.onSavingChange?.(false);
    }
  };

  const handleReorder = (reorderedTemplates: ListVehicleFinancingTemplateResponseBody[]) => {
    // Defence in depth behind the hidden drag handle: a reorder requested without the permission
    // is refused here, so no programmatic call can reach the endpoint.
    if (!canReorderTemplates) {
      return;
    }

    const type = head(reorderedTemplates)?.type;

    if (isNil(type)) {
      return;
    }

    void reorderTemplates({
      reorderAutomaticVehicleFinancingTemplatesRequestBody: {
        type,
        positions: buildAutomaticOrderPositions(
          templatesQuery.data ?? [],
          type,
          reorderedTemplates
        ),
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const renderExistingTemplate = (
    template: ListVehicleFinancingTemplateResponseBody,
    handleProps?: HTMLAttributes<HTMLDivElement>
  ) => (
    <ExistingTemplateCard
      key={template.id}
      template={template}
      templates={templatesQuery.data ?? []}
      // A reorder in flight is blocked via `disabledDrag` instead, so the handle stays put rather
      // than disappearing and re-appearing under the cursor.
      handleProps={canReorderTemplates && !hasPendingEditor ? handleProps : undefined}
      isReorderingAvailable={canReorderTemplates}
      isExpanded={activeExpandedTemplateId === template.id}
      hasPendingEditor={hasPendingEditor}
      onToggle={() =>
        discardPendingEditorBefore(() =>
          setExpandedTemplateId(activeExpandedTemplateId === template.id ? null : template.id)
        )
      }
      onCloned={async (clonedTemplate) => {
        await props.onCreated?.(clonedTemplate);
        setExpandedTemplateId(clonedTemplate.id);
      }}
      onArchived={() => {
        if (activeExpandedTemplateId === template.id) {
          setExpandedTemplateId(null);
        }
      }}
      onDeleted={() => {
        if (activeExpandedTemplateId === template.id) {
          setExpandedTemplateId(null);
        }
      }}
      onDiscard={() => {
        publishPendingEditor(null);
        setExpandedTemplateId(null);
      }}
      onSavingChange={props.onSavingChange}
      onSaved={props.onUpdated}
      onPendingChange={publishPendingEditor}
    />
  );

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={() => discardPendingEditorBefore(props.onClose)}
      title={
        isNotNil(expandedTemplateName)
          ? i18n.t('page.vehicle.financingAndLeasing.configurationDialogTitle', {
              name: expandedTemplateName,
            })
          : i18n.t('entity.vehicleFinancingTemplate.labels.pageTitle')
      }
      size="xLarge"
      disableMaxHeight
      buttons={[
        {
          title: i18n.t('general.actions.close'),
          variant: 'secondary',
          onClick: () => discardPendingEditorBefore(props.onClose),
        },
      ]}
    >
      <VStack spacing={4}>
        <HStack justify="flex-end">
          <Button
            title={i18n.t('entity.vehicleFinancingTemplate.actions.createTemplate')}
            leftIcon="content/add_circle"
            isDisabled={!canCreateTemplate || isNewTemplateVisible}
            onClick={() => discardPendingEditorBefore(handleCreateTemplate)}
          />
        </HStack>

        <DataStatus isLoading={templatesQuery.isLoading} isError={templatesQuery.isError}>
          <VStack spacing={3}>
            <Text color="secondary">
              {i18n.t('entity.vehicleFinancingTemplate.descriptions.orderExplanation')}
            </Text>
            <Show when={(templatesQuery.data?.length ?? 0) === 0 && !isNewTemplateOpen}>
              <Card variant="inlineGrey">
                <Text color="secondary">
                  {i18n.t('entity.vehicleFinancingTemplate.labels.noTemplatesYet')}
                </Text>
              </Card>
            </Show>

            <Show when={isNewTemplateVisible}>
              <Card variant="inlineGrey" data-testid="financing-template-new">
                <TemplateCardHeader
                  title={i18n.t('entity.vehicleFinancingTemplate.labels.createPageTitle')}
                  titleIcon="action/credit_card"
                  flags={[
                    {
                      label: i18n.t('general.labels.new'),
                      colorScheme: 'neutral',
                      isSubtle: true,
                    },
                    {
                      label: getTemplateTypeLabel(props.fallbackTemplate.type),
                      colorScheme: 'blue',
                      isSubtle: true,
                    },
                  ]}
                  isReorderingAvailable={canReorderTemplates}
                  isExpanded={activeExpandedTemplateId === newTemplateCardId}
                  onToggle={() =>
                    discardPendingEditorBefore(() =>
                      setExpandedTemplateId(
                        activeExpandedTemplateId === newTemplateCardId ? null : newTemplateCardId
                      )
                    )
                  }
                  data-testid="financing-template-new"
                >
                  <VehicleFinancingTemplateForm
                    key={NEW_TEMPLATE_ID}
                    template={props.fallbackTemplate}
                    isReadOnly={!canCreateTemplate}
                    isEmbedded
                    isBulkEditEnabled
                    onSubmit={handleNewTemplateSubmit}
                    renderFooter={(footer) => (
                      <EmbeddedTemplateFormFooter
                        footer={footer}
                        onDiscard={() => {
                          footer.discard();
                          publishPendingEditor(null);
                          setIsNewTemplateOpen(false);
                          setCreatedTemplateId(null);
                          setExpandedTemplateId(null);
                        }}
                      />
                    )}
                    onDirtyChange={publishPendingEditor}
                    data-testid="financing-template-new-form"
                  />
                </TemplateCardHeader>
              </Card>
            </Show>

            <VStack spacing={6}>
              <Show
                when={
                  standardTemplates.inAutomaticOrder.length > 0 ||
                  standardActiveNotEnrolledTemplates.length > 0
                }
              >
                <VStack spacing={3} data-testid="financing-templates-section-standard">
                  <Heading size={4}>{getFinancingTypeLabel('STANDARD')}</Heading>
                  <SortableCardList
                    withHandle
                    disableDropAnimation
                    disabledDrag={reorderStatus.isLoading}
                    containerId="vehicle-standard-financing-template-order"
                    items={standardTemplates.inAutomaticOrder}
                    onReorder={handleReorder}
                    renderCard={(template, _index, handleProps) =>
                      renderExistingTemplate(template, handleProps)
                    }
                  />
                  {standardActiveNotEnrolledTemplates.map((template) =>
                    renderExistingTemplate(template)
                  )}
                </VStack>
              </Show>

              <Show
                when={
                  balloonTemplates.inAutomaticOrder.length > 0 ||
                  balloonActiveNotEnrolledTemplates.length > 0
                }
              >
                <VStack spacing={3} data-testid="financing-templates-section-balloon">
                  <Heading size={4}>{getFinancingTypeLabel('BALLOON')}</Heading>
                  <SortableCardList
                    withHandle
                    disableDropAnimation
                    disabledDrag={reorderStatus.isLoading}
                    containerId="vehicle-balloon-financing-template-order"
                    items={balloonTemplates.inAutomaticOrder}
                    onReorder={handleReorder}
                    renderCard={(template, _index, handleProps) =>
                      renderExistingTemplate(template, handleProps)
                    }
                  />
                  {balloonActiveNotEnrolledTemplates.map((template) =>
                    renderExistingTemplate(template)
                  )}
                </VStack>
              </Show>

              <Show when={archivedTemplates.length > 0}>
                <VStack spacing={3} data-testid="financing-templates-section-archived">
                  <Heading size={4}>
                    {i18n.t('entity.vehicleFinancingTemplate.status.archived')}
                  </Heading>
                  {archivedTemplates.map((template) => renderExistingTemplate(template))}
                </VStack>
              </Show>
            </VStack>
          </VStack>
        </DataStatus>
      </VStack>
    </Dialog>
  );
}
